/**
 * @fileoverview Helpers puros do UserScheduleField — conversão entre o formato
 * salvo em `User.workingHours` (faixas achatadas; `weekdayEn` repetido = turno
 * partido, o almoço é o vão) e os grupos de edição (expediente + almoço
 * opcional), mais a validação dos grupos. Sem React, sem MUI: testável em
 * isolamento (ver __tests__/helpers.test.js).
 *
 * Contrato com o backend (validateWorkingHours): no máximo 2 faixas por dia,
 * sem sobreposição, HH:MM com início < fim. Aqui garantimos que o que sai do
 * editor sempre cabe nesse contrato e que o que entra (via API) é exibido sem
 * perda: faixas encostadas (09–12 + 12–18) são fundidas em um expediente
 * contínuo em vez de virarem um "almoço 12:00–12:00" inválido.
 */

export const ALL_DAYS = [
  { weekday: "Segunda-feira", weekdayEn: "monday", short: "Seg" },
  { weekday: "Terça-feira", weekdayEn: "tuesday", short: "Ter" },
  { weekday: "Quarta-feira", weekdayEn: "wednesday", short: "Qua" },
  { weekday: "Quinta-feira", weekdayEn: "thursday", short: "Qui" },
  { weekday: "Sexta-feira", weekdayEn: "friday", short: "Sex" },
  { weekday: "Sábado", weekdayEn: "saturday", short: "Sáb" },
  { weekday: "Domingo", weekdayEn: "sunday", short: "Dom" },
];

export const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
export const ALL_WEEKDAYS_EN = ALL_DAYS.map((d) => d.weekdayEn);

export const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

// react-number-format (format "##:##", sem mask) entrega valores parciais com
// espaços nas posições vazias: "0 :  ", "09:0 ". Normalizamos: sem dígito =
// vazio; com dígitos = o valor como está (completo só com 4 dígitos).
const digitsOf = (v) => String(v || "").replace(/\D/g, "");
export const normalizeTime = (v) => (digitsOf(v).length ? String(v) : "");
export const isTimeComplete = (v) => digitsOf(v).length === 4;

export const toMinutes = (hhmm) => {
  if (!TIME_RE.test(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

export const makeId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Funde faixas encostadas ou sobrepostas do mesmo dia (já ordenadas).
const mergeContiguous = (ivs) => {
  const out = [];
  ivs.forEach((iv) => {
    const last = out[out.length - 1];
    if (last && toMinutes(iv.s) <= toMinutes(last.e)) {
      if (toMinutes(iv.e) > toMinutes(last.e)) last.e = iv.e;
      return;
    }
    out.push({ s: iv.s, e: iv.e });
  });
  return out;
};

// value (array salvo, faixas achatadas) → grupos de edição (expediente + almoço).
// Reconstrói o almoço quando o dia tem 2 faixas: o vão entre elas vira o almoço.
export const scheduleValueToGroups = (value) => {
  if (!Array.isArray(value) || value.length === 0) return [];

  const byDay = {};
  value.forEach((d) => {
    if (!d || !d.weekdayEn) return;
    if (toMinutes(d.startTime) === null || toMinutes(d.endTime) === null) return;
    if (!byDay[d.weekdayEn]) byDay[d.weekdayEn] = [];
    byDay[d.weekdayEn].push({ s: d.startTime, e: d.endTime });
  });

  const sigByDay = {};
  Object.keys(byDay).forEach((day) => {
    const ivs = mergeContiguous(
      byDay[day].slice().sort((a, b) => toMinutes(a.s) - toMinutes(b.s))
    );
    if (ivs.length === 1) {
      sigByDay[day] = {
        startTime: ivs[0].s,
        endTime: ivs[0].e,
        lunchStart: "",
        lunchEnd: "",
      };
    } else {
      // 2 faixas: expediente = 1ª.início → última.fim; almoço = vão 1ª.fim → 2ª.início.
      // (3+ faixas não passam no backend; se aparecer, o 1º vão vira o almoço.)
      sigByDay[day] = {
        startTime: ivs[0].s,
        endTime: ivs[ivs.length - 1].e,
        lunchStart: ivs[0].e,
        lunchEnd: ivs[1].s,
      };
    }
  });

  const map = {};
  const order = [];
  ALL_DAYS.forEach((d) => {
    const sig = sigByDay[d.weekdayEn];
    if (!sig) return;
    const key = `${sig.startTime}|${sig.endTime}|${sig.lunchStart}|${sig.lunchEnd}`;
    if (!map[key]) {
      map[key] = { id: makeId(), days: [], ...sig };
      order.push(key);
    }
    map[key].days.push(d.weekdayEn);
  });
  return order.map((k) => map[k]);
};

// grupos → array salvável. Almoço preenchido → 2 faixas (manhã + tarde); sem
// almoço → 1 faixa. Segmentos vazios/invertidos são descartados.
export const scheduleGroupsToValue = (groups) => {
  const out = [];
  (groups || []).forEach((g) => {
    const startTime = normalizeTime(g.startTime);
    const endTime = normalizeTime(g.endTime);
    const lunchStart = normalizeTime(g.lunchStart);
    const lunchEnd = normalizeTime(g.lunchEnd);
    if (!startTime || !endTime) return;
    const segments =
      lunchStart && lunchEnd
        ? [
            [startTime, lunchStart],
            [lunchEnd, endTime],
          ]
        : [[startTime, endTime]];
    segments.forEach(([s, e]) => {
      const sm = toMinutes(s);
      const em = toMinutes(e);
      if (sm === null || em === null || sm >= em) return;
      (g.days || []).forEach((weekdayEn) => {
        const meta = ALL_DAYS.find((x) => x.weekdayEn === weekdayEn);
        out.push({
          weekday: meta ? meta.weekday : weekdayEn,
          weekdayEn,
          startTime: s,
          endTime: e,
        });
      });
    });
  });
  return out;
};

// Erro por grupo (null = ok). Mensagens em pt-BR prontas para a UI.
export const computeGroupErrors = (groups) => {
  const errors = {};
  (groups || []).forEach((g) => {
    if (!g.days || !g.days.length) {
      errors[g.id] = "Selecione pelo menos um dia";
      return;
    }
    const startTime = normalizeTime(g.startTime);
    const endTime = normalizeTime(g.endTime);
    if (!startTime || !endTime) {
      errors[g.id] = "Preencha o expediente (início e fim)";
      return;
    }
    const s = toMinutes(startTime);
    const e = toMinutes(endTime);
    if (s === null || e === null) {
      errors[g.id] = "Expediente: formato inválido (HH:MM)";
      return;
    }
    if (s >= e) {
      errors[g.id] =
        "Expediente: fim deve ser maior que início (turno que cruza a meia-noite não é suportado)";
      return;
    }
    const lunchStart = normalizeTime(g.lunchStart);
    const lunchEnd = normalizeTime(g.lunchEnd);
    if (lunchStart || lunchEnd) {
      if (!lunchStart || !lunchEnd) {
        errors[g.id] = "Almoço: preencha início e fim (ou deixe ambos vazios)";
        return;
      }
      const ls = toMinutes(lunchStart);
      const le = toMinutes(lunchEnd);
      if (ls === null || le === null) {
        errors[g.id] = "Almoço: formato inválido (HH:MM)";
        return;
      }
      if (ls >= le) {
        errors[g.id] = "Almoço: fim deve ser maior que início";
        return;
      }
      // Estritamente dentro: almoço que começa/termina junto com o expediente
      // (ou o cobre inteiro) apagaria o dia silenciosamente ao salvar.
      if (ls <= s || le >= e) {
        errors[g.id] =
          "O almoço precisa ficar dentro do expediente, sem coincidir com o início ou o fim";
        return;
      }
    }
    errors[g.id] = null;
  });
  return errors;
};

export const scheduleGroupsHaveErrors = (groups) =>
  Object.values(computeGroupErrors(groups)).some(Boolean);
